import { Capacitor } from '@capacitor/core';

/**
 * A tutorial video's soundtrack is the instructor's voice: the narration
 * channel. Browsers and the Android app's web view play a video at its
 * element `volume`. The iOS app's web view ignores that property (only `muted`
 * works there), so on iOS a video below full narration plays through a Web
 * Audio gain instead. A video at full or at zero is left as it is: it plays at
 * full by itself, and `muted` silences it. Once a video plays through its
 * gain, its element volume stays at full, so a web view that does apply it
 * cannot lower the level twice.
 */

type NarrationRoute = { gain: GainNode; context: AudioContext };

const routesByVideo = new WeakMap<HTMLMediaElement, NarrationRoute>();
// The routed videos playing now. The shared context runs only while one is,
// so a paused tutorial does not keep the audio running in the background.
const playingRoutedVideos = new Set<HTMLMediaElement>();
let narrationContext: AudioContext | undefined;

/** Whether this platform ignores a video element's `volume` (the iOS app). */
export const videoVolumeIgnored = (): boolean => Capacitor.getPlatform() === 'ios';

/**
 * The `crossOrigin` a tutorial video needs. Web Audio hears a video from
 * another origin only when it was fetched with CORS; without it the gain
 * would play silence.
 */
export const narrationVideoCrossOrigin = (): 'anonymous' | undefined =>
  videoVolumeIgnored() ? 'anonymous' : undefined;

const resume = (context: AudioContext): void => {
  if (context.state === 'running') return;
  context.resume().catch(() => {
    // Resumed on the next play, volume change or state change.
  });
};

const suspend = (context: AudioContext): void => {
  if (context.state !== 'running') return;
  context.suspend().catch(() => {
    // Suspended on the next pause.
  });
};

/** Runs the context while a routed video plays, and suspends it once none does. */
const matchContextToPlayback = (context: AudioContext): void => {
  if (playingRoutedVideos.size > 0) resume(context);
  else suspend(context);
};

const audioContextForNarration = (): AudioContext | undefined => {
  if (narrationContext && narrationContext.state !== 'closed') return narrationContext;
  if (typeof window.AudioContext !== 'function') return undefined;
  const context = new window.AudioContext();
  // The system suspends or interrupts the context mid-play (backgrounding, a
  // call): bring it back while a routed video still plays. A resume that lands
  // after the last video has stopped is suspended again, so the context does
  // not run idle.
  context.addEventListener('statechange', () => matchContextToPlayback(context));
  narrationContext = context;
  return context;
};

const followPlayback = (video: HTMLMediaElement, context: AudioContext): void => {
  const played = () => {
    playingRoutedVideos.add(video);
    matchContextToPlayback(context);
  };
  const stopped = () => {
    playingRoutedVideos.delete(video);
    matchContextToPlayback(context);
  };
  video.addEventListener('play', played);
  video.addEventListener('pause', stopped);
  video.addEventListener('ended', stopped);
  // A new source stops a playing video without a pause event.
  video.addEventListener('emptied', stopped);
  if (!video.paused) playingRoutedVideos.add(video);
};

const routeThroughGain = (video: HTMLMediaElement): NarrationRoute | undefined => {
  const context = audioContextForNarration();
  if (!context) return undefined;
  try {
    const source = context.createMediaElementSource(video);
    const gain = context.createGain();
    source.connect(gain);
    gain.connect(context.destination);
    followPlayback(video, context);
    const route = { gain, context };
    routesByVideo.set(video, route);
    return route;
  } catch {
    // Left to its own output: the slider then acts at zero only.
    return undefined;
  }
};

const needsGainRoute = (gain: number): boolean => gain > 0 && gain < 1;

/** Plays a tutorial video's narration at this gain (0 to 1). */
export const applyVideoNarrationGain = (video: HTMLMediaElement, gain: number): void => {
  const route = videoVolumeIgnored()
    ? (routesByVideo.get(video) ?? (needsGainRoute(gain) ? routeThroughGain(video) : undefined))
    : undefined;
  if (!route) {
    video.volume = gain;
    return;
  }
  video.volume = 1;
  route.gain.gain.value = gain;
  matchContextToPlayback(route.context);
};
